// src/components/surveys/preview/SurveyPreview.tsx
import React, { useState, useCallback } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Send,
  X,
  CheckCircle,
  AlertCircle,
  UserX,
} from 'lucide-react';
import { Survey, SurveyQuestion } from '../../../types/survey.types';
import { QuestionRenderer } from '../questions';

interface SurveyPreviewProps {
  survey: Survey;
  onClose?: () => void;
  onSubmit?: (responses: Record<string, any>) => void;
  onDiscontinued?: (partialResponses: Record<string, any>) => void;
  // Fired when a yes_no question with `triggerLeadCapture` is answered "yes".
  // The canvass page uses this to close the survey and open the full lead form.
  onLeadCaptureTriggered?: (partialResponses: Record<string, any>) => void;
  mode?: 'preview' | 'response';
}

const SurveyPreview: React.FC<SurveyPreviewProps> = ({
  survey,
  onClose,
  onSubmit,
  onDiscontinued,
  onLeadCaptureTriggered,
  mode = 'preview',
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Helper to evaluate a single condition against responses
  const evaluateSingleCondition = (sourceQuestionId: string, operator: string, value: any): boolean => {
    const sourceAnswer = responses[sourceQuestionId];

    switch (operator) {
      case 'equals':
        // Case-insensitive comparison for strings
        if (typeof sourceAnswer === 'string' && typeof value === 'string') {
          return sourceAnswer.toLowerCase() === value.toLowerCase();
        }
        return sourceAnswer === value;
      case 'not_equals':
        // Case-insensitive comparison for strings
        if (typeof sourceAnswer === 'string' && typeof value === 'string') {
          return sourceAnswer.toLowerCase() !== value.toLowerCase();
        }
        return sourceAnswer !== value;
      case 'contains':
        return Array.isArray(sourceAnswer)
          ? sourceAnswer.includes(value)
          : String(sourceAnswer || '').includes(String(value));
      case 'not_contains':
        return Array.isArray(sourceAnswer)
          ? !sourceAnswer.includes(value)
          : !String(sourceAnswer || '').includes(String(value));
      case 'greater_than':
        return Number(sourceAnswer) > Number(value);
      case 'less_than':
        return Number(sourceAnswer) < Number(value);
      case 'is_empty':
        return !sourceAnswer || (Array.isArray(sourceAnswer) && sourceAnswer.length === 0);
      case 'is_not_empty':
        return !!sourceAnswer && (!Array.isArray(sourceAnswer) || sourceAnswer.length > 0);
      case 'in_list':
        const listValues = Array.isArray(value) ? value : [value];
        return listValues.includes(sourceAnswer);
      case 'not_in_list':
        const notListValues = Array.isArray(value) ? value : [value];
        return !notListValues.includes(sourceAnswer);
      default:
        return false;
    }
  };

  // Helper to evaluate a rule - supports both flat structure (from editor) and nested structure (from types)
  const evaluateRule = (rule: any): boolean => {
    // Check if rule uses flat structure (from ConditionalLogicEditor)
    if (rule.sourceQuestionId && rule.operator !== undefined) {
      return evaluateSingleCondition(rule.sourceQuestionId, rule.operator, rule.conditionValue);
    }

    // Check if rule uses nested structure (from types definition)
    if (rule.conditions && Array.isArray(rule.conditions)) {
      const conditionOperator = rule.conditionOperator || 'and';
      if (conditionOperator === 'and') {
        return rule.conditions.every((c: any) =>
          evaluateSingleCondition(c.sourceQuestionId, c.operator, c.value)
        );
      } else {
        return rule.conditions.some((c: any) =>
          evaluateSingleCondition(c.sourceQuestionId, c.operator, c.value)
        );
      }
    }

    return false;
  };

  // Helper to get target question ID(s) from a rule - supports both structures
  const getRuleTargetIds = (rule: any): string[] => {
    if (rule.targetQuestionId) {
      return [rule.targetQuestionId];
    }
    if (rule.targetQuestionIds && Array.isArray(rule.targetQuestionIds)) {
      return rule.targetQuestionIds;
    }
    return [];
  };

  const visibleQuestions = survey.questions.filter((q) => {
    // Check conditional logic to determine visibility
    const applicableRules = (survey.conditionalLogic || []).filter(
      (rule: any) => getRuleTargetIds(rule).includes(q.id)
    );

    for (const rule of applicableRules) {
      const conditionsMet = evaluateRule(rule);

      if (conditionsMet) {
        if (rule.action === 'hide') return false;
        if (rule.action === 'show') return true;
      }
    }

    return q.defaultVisible !== false;
  });

  const currentQuestion = visibleQuestions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / visibleQuestions.length) * 100;

  const validateCurrentQuestion = useCallback((): boolean => {
    if (!currentQuestion) return true;

    const value = responses[currentQuestion.id];
    let error = '';

    if (currentQuestion.required) {
      if (value === undefined || value === null || value === '') {
        error = 'This question is required';
      } else if (Array.isArray(value) && value.length === 0) {
        error = 'Please select at least one option';
      }
    }

    // Additional validation based on question type
    if (!error && value) {
      switch (currentQuestion.type) {
        case 'email':
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            error = 'Please enter a valid email address';
          }
          break;
        case 'phone':
          const digits = (value as string).replace(/\D/g, '');
          if (digits.length !== 10) {
            error = 'Please enter a valid 10-digit phone number';
          }
          break;
        case 'text':
          const minLength = currentQuestion.validation?.minLength;
          if (minLength && value.length < minLength) {
            error = `Minimum ${minLength} characters required`;
          }
          break;
        case 'number':
          const min = currentQuestion.validation?.min;
          const max = currentQuestion.validation?.max;
          if (min !== undefined && value < min) {
            error = `Value must be at least ${min}`;
          } else if (max !== undefined && value > max) {
            error = `Value must be at most ${max}`;
          }
          break;
      }
    }

    if (error) {
      setErrors((prev) => ({ ...prev, [currentQuestion.id]: error }));
      return false;
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[currentQuestion.id];
        return newErrors;
      });
      return true;
    }
  }, [currentQuestion, responses]);

  const handleNext = () => {
    if (!validateCurrentQuestion()) return;

    // Lead-capture gate: if this is a yes_no question with triggerLeadCapture
    // and the user answered "yes", hand off to the lead form instead of
    // advancing to the next question.
    if (
      currentQuestion?.triggerLeadCapture &&
      currentQuestion.type === 'yes_no' &&
      responses[currentQuestion.id] === 'yes' &&
      onLeadCaptureTriggered
    ) {
      onLeadCaptureTriggered({ ...responses });
      return;
    }

    // Check for skip_to rules in conditionalLogic (flat structure from editor)
    const skipToRules = (survey.conditionalLogic || []).filter(
      (rule: any) => rule.action === 'skip_to' && rule.sourceQuestionId === currentQuestion?.id
    );

    for (const rule of skipToRules) {
      const conditionMet = evaluateRule(rule);
      if (conditionMet) {
        const targetId = rule.targetQuestionId || rule.skipToQuestionId;
        const skipToIndex = visibleQuestions.findIndex((q) => q.id === targetId);
        if (skipToIndex !== -1) {
          setCurrentQuestionIndex(skipToIndex);
          return;
        }
      }
    }

    // Check for skip patterns (nested structure from types)
    const applicableSkips = (survey.skipPatterns || []).filter(
      (pattern: any) => pattern.fromQuestionId === currentQuestion?.id
    );

    for (const pattern of applicableSkips) {
      let shouldSkip = false;

      // Support both flat and nested structure for skip patterns
      if (pattern.conditions && Array.isArray(pattern.conditions)) {
        const conditionOperator = pattern.conditionOperator || 'and';
        if (conditionOperator === 'and') {
          shouldSkip = pattern.conditions.every((c: any) =>
            evaluateSingleCondition(c.sourceQuestionId, c.operator, c.value)
          );
        } else {
          shouldSkip = pattern.conditions.some((c: any) =>
            evaluateSingleCondition(c.sourceQuestionId, c.operator, c.value)
          );
        }
      }

      if (shouldSkip) {
        // Handle end survey skip type
        if (pattern.skipType === 'end_survey') {
          setCurrentQuestionIndex(visibleQuestions.length - 1);
          return;
        }

        // Handle skip to specific question
        const skipToIndex = visibleQuestions.findIndex(
          (q) => q.id === pattern.toQuestionId
        );
        if (skipToIndex !== -1) {
          setCurrentQuestionIndex(skipToIndex);
          return;
        }
      }
    }

    if (currentQuestionIndex < visibleQuestions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    // Support both field names for backward compatibility
    const allowBack = survey.settings.allowBack ?? survey.settings.allowBackNavigation ?? true;
    if (allowBack && currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handleSubmit = () => {
    if (!validateCurrentQuestion()) return;

    // Only validate required questions that the user actually answered
    // Questions that were skipped via skip logic should not block submission
    let hasErrors = false;
    const answeredQuestionIds = Object.keys(responses);

    for (const questionId of answeredQuestionIds) {
      const question = visibleQuestions.find(q => q.id === questionId);
      if (question?.required) {
        const value = responses[questionId];
        if (value === undefined || value === null || value === '' ||
            (Array.isArray(value) && value.length === 0)) {
          setErrors((prev) => ({
            ...prev,
            [questionId]: 'This question is required',
          }));
          hasErrors = true;
        }
      }
    }

    if (hasErrors) return;

    if (mode === 'response' && onSubmit) {
      onSubmit(responses);
    }
    setIsSubmitted(true);
  };

  const handleResponseChange = (value: any) => {
    if (!currentQuestion) return;
    setResponses((prev) => ({
      ...prev,
      [currentQuestion.id]: value,
    }));
    // Clear error when user starts typing
    if (errors[currentQuestion.id]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[currentQuestion.id];
        return newErrors;
      });
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {mode === 'preview' ? 'Preview Complete' : 'Survey Submitted!'}
          </h2>
          <p className="text-gray-600 mb-6">
            {survey.settings.thankYouMessage || survey.settings.completionMessage || 'Thank you for completing the survey!'}
          </p>
          {onClose && (
            <button
              onClick={onClose}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-xl font-semibold text-gray-900">{survey.name}</h1>
          <div className="flex items-center gap-2">
            {/* Discontinued button - for when respondent walks away */}
            {mode === 'response' && onDiscontinued && (
              <button
                onClick={() => onDiscontinued(responses)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg text-sm font-medium transition-colors"
              >
                <UserX className="h-4 w-4" />
                Discontinued
              </button>
            )}
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        {/* Progress bar */}
        {(survey.settings.showProgressBar ?? survey.settings.showProgress) && (
          <div className="h-1 bg-gray-200">
            <div
              className="h-full bg-indigo-600 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>

      {/* Question content */}
      <div className="max-w-2xl mx-auto px-6 py-12">
        {currentQuestion ? (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* Question number */}
            <div className="text-sm text-indigo-600 font-medium mb-2">
              Question {currentQuestionIndex + 1} of {visibleQuestions.length}
            </div>

            {/* Question text */}
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {currentQuestion.text}
              {currentQuestion.required && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </h2>

            {/* Question description */}
            {currentQuestion.description && (
              <p className="text-gray-500 mb-6">{currentQuestion.description}</p>
            )}

            {/* Question input */}
            <div className="mb-8">
              <QuestionRenderer
                question={currentQuestion}
                value={responses[currentQuestion.id]}
                onChange={handleResponseChange}
                showValidation={!!errors[currentQuestion.id]}
                error={errors[currentQuestion.id]}
                mode="response"
              />
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between pt-6 border-t border-gray-100">
              <button
                onClick={handlePrevious}
                disabled={currentQuestionIndex === 0 || !(survey.settings.allowBack ?? survey.settings.allowBackNavigation ?? true)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                  currentQuestionIndex === 0 || !(survey.settings.allowBack ?? survey.settings.allowBackNavigation ?? true)
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <ChevronLeft className="h-5 w-5" />
                Back
              </button>

              {currentQuestionIndex === visibleQuestions.length - 1 ? (
                <button
                  onClick={handleSubmit}
                  className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  <Send className="h-5 w-5" />
                  {survey.settings.submitButtonText || 'Submit'}
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Next
                  <ChevronRight className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              No questions available
            </h2>
            <p className="text-gray-500">
              This survey doesn't have any questions yet.
            </p>
          </div>
        )}
      </div>

      {/* Preview mode indicator */}
      {mode === 'preview' && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full text-sm font-medium shadow-lg">
          Preview Mode - Responses will not be saved
        </div>
      )}
    </div>
  );
};

export default SurveyPreview;
